import type { H3PlaneTotals } from "./h3Fleet";

// The history window the GPU Fleet page filters over — the presets the dashboard
// API serves through `?window_hours=` — and the small helpers the window panels
// (chart, worker cards, clip list) share. Pure functions, no React.

export type WindowPreset = { hours: number; label: string };

export const WINDOW_PRESETS: readonly WindowPreset[] = [
  { hours: 1, label: "1 h" },
  { hours: 6, label: "6 h" },
  { hours: 24, label: "24 h" },
  { hours: 168, label: "7 d" },
  { hours: 720, label: "30 d" },
];

/** "24 h" / "7 d", or the raw figure for a window the API clamped to. */
export function windowLabel(hours: number): string {
  const preset = WINDOW_PRESETS.find((entry) => entry.hours === hours);
  if (preset) return preset.label;
  return hours % 24 === 0 ? `${hours / 24} d` : `${hours} h`;
}

/** The CRM page of the Echo a clip belongs to; the session id is its workload id. */
export function echoPath(workloadId: string): string {
  return `/echoflicks_video_sessions/${encodeURIComponent(workloadId)}/show`;
}

/** "41%"; a share under half a percent reads "<1%" so a quiet GPU is not "0%". */
export function formatShare(share: number | null | undefined): string {
  if (share === null || share === undefined || !Number.isFinite(share)) {
    return "—";
  }
  const percent = share * 100;
  if (percent > 0 && percent < 1) return "<1%";
  return `${Math.round(percent)}%`;
}

/** Axis label for a bucket start: the clock for sub-day buckets, the date for days. */
export function bucketLabel(
  startIso: string,
  bucketSeconds: number,
  timeZone?: string,
): string {
  const date = new Date(startIso);
  if (Number.isNaN(date.getTime())) return "";
  if (bucketSeconds >= 86_400) {
    return date.toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      timeZone,
    });
  }
  // A 24-hour clock whatever the browser's locale: operators read these against
  // UTC log timestamps.
  if (bucketSeconds >= 6 * 3600) {
    return date.toLocaleString(undefined, {
      weekday: "short",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
      timeZone,
    });
  }
  return date.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
    timeZone,
  });
}

/** Why a clip failed, in the words of the page. Unknown reasons pass through. */
export function failureLabel(reason: string | null): string {
  switch (reason) {
    case "heartbeat_lost":
      return "worker stopped heartbeating";
    case "claim_timeout":
      return "nobody claimed it in time";
    case "no_worker_timeout":
      return "no worker in time — scene went to the fallback provider";
    case "budget_exhausted":
      return "queue timeout";
    case "cancel_requested":
      return "cancelled";
    case "redispatch_impossible":
      return "could not be re-posted";
    case null:
      return "no reason logged";
    default:
      return reason;
  }
}

/** Share of the delivered clips your own GPUs rendered, 0..1; null when nothing was delivered. */
export function selfHostedShare(
  byPlane: Record<string, H3PlaneTotals> | undefined,
): number | null {
  if (!byPlane) return null;
  const delivered = Object.values(byPlane).reduce(
    (sum, totals) => sum + totals.completed,
    0,
  );
  if (delivered === 0) return null;
  return (byPlane.self_hosted?.completed ?? 0) / delivered;
}
